import React, { useEffect, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import {
  Autocomplete,
  BackLink,
  Button,
  IconButton,
  Typography,
} from '../../components/atoms';
import { ArrowLeft as ArrowLeftIcon } from '../../components/icons';
import { BottomBar, Dialog, Hidden, Page } from '../../components/layouts';
import { AddressFormat } from '../../components/molecules';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services';
import { domUtils, urlUtils, utils } from '../../utils';
import * as Api from '../../weborder/Api';
import { DispatchProps, StateProps } from '../../weborder/component/Map';
import * as Data from '../../weborder/Data';
import { LoadMaps } from '../../weborder/MapLoader';
import { MapNotice } from './components/MapNotice';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  autocomplete: {
    '&.MuiAutocomplete-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      margin: theme.spacing(0, 2, 2),
    },
  },

  dialogForMobile: {
    '& .MuiDialogTitle-root': {
      display: 'none',
    },
    '& .MuiDialogContent-root': {
      padding: 0,
    },
    '& .MuiDialogContent-root .MuiFormControl-root': {
      margin: 0,
    },
    '& .MuiInputBase-adornedStart:not(.Mui-error)': {
      border: 'none',
    },
  },

  dummyPlaceholder: {
    color: theme.palette.grey[600],
  },

  dummyTextfield: {
    border: `1px solid ${theme.palette.grey[400]}`,
    margin: theme.spacing(0, 2, 2),
    padding: theme.spacing(1.5, 1.25),
  },

  geolocationButton: {
    color: theme.palette.grey[800],
    display: 'inline-flex',
    margin: theme.spacing(0, 0, 2.75, 2),

    '& button': {
      marginLeft: theme.spacing(0.625),
    },
  },

  map: {
    boxSizing: 'border-box',
    height: '260px',
    marginBottom: theme.spacing(2),
    width: '100%',

    '&:after': {
      background:
        'url("https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi_hdpi.png")',
      backgroundSize: '22px 40px',
      content: '""',
      display: 'block',
      height: '40px',
      left: '50%',
      margin: '-18px 0 0 -11px',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      width: '22px',
    },
  },

  mapAlert: {
    left: '50%',
    margin: '-25px 0 0 -100px',
    opacity: 1,
    position: 'absolute',
    top: '25%',

    '&.hideAlert': {
      opacity: 0,
      transition: 'opacity 1s',
    },
  },

  mapWrapper: {
    height: 'auto',
    position: 'relative',
    width: 'auto',
  },

  [theme.breakpoints.up('sm')]: {
    autocomplete: {
      '&.MuiAutocomplete-root': {
        margin: theme.spacing(0, 3, 2),
      },
    },

    geolocationButton: {
      margin: theme.spacing(0, 0, 2.75, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    autocomplete: {
      '&.MuiAutocomplete-root': {
        border: 'none',
        margin: theme.spacing(0, 0, 2),
      },
      '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-child':
        {
          padding: theme.spacing(1.5, 1.25),
        },
    },

    geolocationButton: {
      margin: theme.spacing(0, 0, 2.75),
    },

    map: {
      border: `1px solid ${theme.palette.grey[400]}`,
      height: '200px',
      marginBottom: theme.spacing(2.5),
    },
  },
}));

export function DeliveryCustomLocation(props: Props): JSX.Element {
  const {
    AppConfig: appConfig,
    AddLocation: addLocation,
    Location: location,
    translate,
  } = props;

  const [autocompleteDefaultValue, setAutocompleteDefaultValue] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [dummyPlaceholder, setDummyPlaceholder] = useState(
    translate('Enter_your_address_here'),
  );
  const [geolocationInProgress, setGeolocationInProgress] = useState(false);
  const [map, setMap] = useState(null);
  const [places, setPlaces] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showMapAlert, setShowMapAlert] = useState(true);
  const [showMobileDialog, setShowMobileDialog] = useState(false);

  const locationSelectMethod = useRef('');

  const classes = useStyles();

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    if (appConfig.status === 'success') {
      LoadMaps(appConfig.data.GoogleMapsApiKeyWeb).then(() => {
        initialiseMap();
      });
    }
  }, [appConfig.status]);

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Delivery Custom Location',
    };
    analyticsService.track(eventData);
  };

  const { enqueueSnackbar } = useSnackbar();

  const findUserLocation = () => {
    if (!utils.check.hasLocationServicePermission()) {
      enqueueSnackbar(
        translate('Delivery_snackbarMessage_browserHasNoGeolocation'),
        {
          variant: 'error',
        },
      );

      return;
    }

    if (appConfig.status === 'success') {
      LoadMaps(appConfig.data.GoogleMapsApiKeyWeb).then(() => {
        if (map) {
          locationSelectMethod.current = 'Map';
          setGeolocationInProgress(true);

          navigator.geolocation.getCurrentPosition(
            (p) => handleGeolocationSuccess(map, p),
            handleGeolocationError,
            {
              enableHighAccuracy: true,
            },
          );
        }
      });
    }
  };

  const initialiseMap = () => {
    const options: google.maps.MapOptions = {
      center: Data.LatLng(location),
      clickableIcons: false,
      mapTypeControl: false, // No map type (terrain/satellite, etc)
      streetViewControl: false, // No street view ControlLabel
      zoom: 17,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
    };

    const googleMap = new google.maps.Map(
      document.getElementById('mapRef') as Element,
      options,
    );

    const centerChanged = _debounce(() => {
      const newCenter = Data.ToGeo(googleMap.getCenter());
      const userCertainty = Api.LocationCertainty.User;
      addLocation(newCenter, userCertainty);
    }, 750);

    centerChanged();

    googleMap.addListener('center_changed', centerChanged);

    googleMap.addListener('drag', () => {
      locationSelectMethod.current = 'Map';
      setTimeout(() => setShowMapAlert(false), 2000);
    });

    setMap(googleMap);
    setPlaces(new google.maps.places.PlacesService(googleMap));
  };

  const handleBackArrow = () => setShowMobileDialog(!showMobileDialog);

  const getErrorNote = (error) => {
    let errorNote = '';
    if (error.code === 1) errorNote = translate('Denied_geolocation');
    else if (error.code === 2) errorNote = translate('Permission_geolocation');
    else errorNote = error.message;
    return errorNote;
  };

  const handleGeolocationError = (error: GeolocationPositionError) => {
    const message = `${translate('Error_location')}. ${translate(
      'Enter_address',
    )}. ${translate('Error')} ${getErrorNote(error)}`;

    enqueueSnackbar(message, {
      variant: 'error',
    });

    setGeolocationInProgress(false);
  };

  const handleGeolocationSuccess = (
    googleMap: google.maps.Map,
    position: GeolocationPosition,
  ): void => {
    const newLocation = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude,
    );
    const center = Data.ToGeo(newLocation);

    googleMap.panTo(newLocation);
    setGeolocationInProgress(false);
    addLocation(center, Api.LocationCertainty.GeoLocation);
  };

  const handlePlaceSelected = (_event, selectedLocation) => {
    if (!selectedLocation || selectedLocation.length === 0 || !map || !places) {
      return;
    }

    locationSelectMethod.current = 'Search';
    setDefaultValue(selectedLocation.description);
    setDummyPlaceholder('');
    setShowMobileDialog(!showMobileDialog);

    const request: any = {
      placeId: selectedLocation.place_id,
      fields: ['geometry', 'address_component'],
    };

    places.getDetails(request, (result, _status) => {
      addLocation(
        Data.ToGeo(result.geometry.location),
        Api.LocationCertainty.User,
      );
      map.panTo(result.geometry.location);
    });
  };

  const handleShowMobileDialog = (): void => {
    setShowMobileDialog(!showMobileDialog);
  };

  const navigationEnabled = (): boolean =>
    map !== null && !geolocationInProgress;

  const handleInputChange = _debounce((input) => {
    const inputValue = input.target.value;
    const minGeocodeInputLength = 5;
    if (inputValue.length < minGeocodeInputLength) {
      return;
    }

    setAutocompleteDefaultValue(inputValue);

    if (appConfig.status === 'success') {
      const { data } = appConfig;

      LoadMaps(data.GoogleMapsApiKeyWeb).then((tools) => {
        const bounds = {
          // North East : bigger
          // South West : smaller
          north: data.MapBoundryMaxLatitude,
          south: data.MapBoundryMinLatitude,
          east: data.MapBoundryMaxLongitude,
          west: data.MapBoundryMinLongitude,
        };

        tools.AutoComplete.getPlacePredictions(
          {
            bounds,
            input: inputValue,
            location: Data.LatLng(location),
            radius: 200,
          },
          (newSuggestions: google.maps.places.AutocompletePrediction[]) => {
            if (newSuggestions) {
              setSuggestions(newSuggestions);
            }
          },
        );
      });
    }
  }, 750);

  const geolocationTitle = geolocationInProgress
    ? translate('Finding_location')
    : translate('Use_your_current_location');

  const trackDeliveryLocationEntered = (): void => {
    const eventData = {
      amp_label: 'Delivery Location Entered',
      amp_locationPermissionGiven: utils.check.hasLocationServicePermission(),
      amp_locationSelectionMethod: locationSelectMethod.current,
    };

    analyticsService.track(eventData);
  };

  const handleConfirmAddress = (): void => {
    trackDeliveryLocationEntered();
    urlUtils.navigateToHash(
      `/delivery/custom/${location.Latitude}/${location.Longitude}`,
    );
  };

  const dialogBackButton = (
    <IconButton color="secondary" onClick={handleBackArrow}>
      <ArrowLeftIcon />
    </IconButton>
  );

  const renderAutocomplete = (
    className: string,
    value: string,
    startIcon: JSX.Element,
  ): JSX.Element => (
    <Autocomplete
      className={className}
      defaultValue={value}
      filterOptions={(options) => options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      onChange={handlePlaceSelected}
      onInputChange={handleInputChange}
      options={suggestions}
      placeholder={translate('Enter_your_address_here')}
      renderOption={(option) => <AddressFormat option={option} />}
      startIcon={startIcon}
    />
  );

  const mapAlertStyles = clsx(classes.mapAlert, !showMapAlert && 'hideAlert');

  return (
    <Page pageName="delivery-custom-location">
      <PageHeader
        showProfileMenu={false}
        tabIndex={0}
        title={translate('Type_your_address')}
      >
        <BackLink
          label={translate('Back')}
          linkPath="/delivery/new"
          onClick={trackBackSelected}
        />
      </PageHeader>

      <article className="fd-content__main">
        <Hidden smUp>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div
            aria-label={dummyPlaceholder}
            className={classes.dummyTextfield}
            data-fd="delivery-mobile-dummyTextfield"
            onClick={handleShowMobileDialog}
            role="button"
            tabIndex={0}
          >
            <Typography className={classes.dummyPlaceholder}>
              {dummyPlaceholder}
            </Typography>
            <Typography>{defaultValue}</Typography>
          </div>
        </Hidden>
        <Hidden xsDown>
          {renderAutocomplete(classes.autocomplete, null, null)}
        </Hidden>

        {utils.check.hasLocationServicePermission() && (
          <div className={classes.geolocationButton}>
            <Typography>{translate('Or')} </Typography>
            <Button
              data-fd="geolocation-button"
              disabled={!navigationEnabled()}
              key="geolocation-button"
              onClick={findUserLocation}
              variant="text"
            >
              {geolocationTitle}
            </Button>
          </div>
        )}

        <div className={classes.mapWrapper}>
          <div
            aria-hidden="true"
            className={classes.map}
            data-fd="custom-location-map"
            id="mapRef"
          />
          <MapNotice
            className={mapAlertStyles}
            message={translate('DeliveryCustomLocation_Map_AlertMessage')}
          />
        </div>

        <Hidden smDown>
          <Button
            data-fd="address-confirm"
            fullWidth
            onClick={handleConfirmAddress}
          >
            {translate('Confirm_address')}
          </Button>
        </Hidden>
        {showMobileDialog && (
          <Hidden smUp>
            <Dialog className={classes.dialogForMobile} open={showMobileDialog}>
              {renderAutocomplete(
                null,
                autocompleteDefaultValue,
                dialogBackButton,
              )}
            </Dialog>
          </Hidden>
        )}
      </article>

      <Hidden mdUp>
        <BottomBar fixedOnly={['xs', 'sm']}>
          <Button fullWidth onClick={handleConfirmAddress}>
            {translate('Confirm_address')}
          </Button>
        </BottomBar>
      </Hidden>
    </Page>
  );
}
